<script lang="ts">
  import { SUBSCRIPTION_LIMITS } from '$lib/constants/subscription';
  import { SubscriptionService } from '$lib/services/subscription/SubscriptionService';
  import { onMount } from 'svelte';
  import { useError } from '$lib/hooks/useError';
  
  // Error handling setup
  const { handleError } = useError('TrialExpiringPrompt');
  
  let daysRemaining = 0;
  
  onMount(() => {
    const subscriptionService = SubscriptionService.getInstance();
    const status = subscriptionService.getStatus();
    
    // Get days remaining from trial info
    if (status.trial?.daysRemaining !== undefined) {
      daysRemaining = status.trial.daysRemaining;
    } else {
      // Fallback: calculate from trial end date if available
      const trialInfo = localStorage.getItem('subscription_trial_info');
      if (trialInfo) {
        try {
          const trial = JSON.parse(trialInfo);
          if (trial.endDate) {
            const endDate = new Date(trial.endDate);
            const today = new Date();
            const msPerDay = 24 * 60 * 60 * 1000;
            daysRemaining = Math.max(0, Math.ceil((endDate.getTime() - today.getTime()) / msPerDay));
          }
        } catch (e) {
          handleError(e, {
            severity: 'warning',
            context: { action: 'parseTrialInfo' },
            showToUser: false,
            logToConsole: true
          });
          // Default to 0 days remaining if parsing fails
          daysRemaining = 0;
        }
      }
    }
    
  });
</script>

<div class="prompt-content">
  <div class="trial-expiring-container">
    <div class="trial-expiring-badge">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10"></circle>
        <path d="M12 6v6l4 2"></path>
      </svg>
      <span>체험 {daysRemaining}일 남음</span>
    </div>
    
    <h4 class="trial-expiring-title">무료 체험이 곧 종료됩니다</h4>
    <p class="trial-expiring-subtitle">체험 종료 후 무료 플랜이 적용됩니다</p>
  </div>
  
  <div class="plan-comparison">
    <div class="comparison-header">
      <span class="comparison-label">체험 종료 시 제한되는 기능</span>
    </div>
    <div class="feature-limits">
      <div class="limit-item">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="8" y1="12" x2="16" y2="12"></line>
        </svg>
        <span>맞춤법 검사 하루 {SUBSCRIPTION_LIMITS.FREE.dailySpellCheckLimit}회로 제한</span>
      </div>
      <div class="limit-item">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="8" y1="12" x2="16" y2="12"></line>
        </svg>
        <span>AI 분석 하루 {SUBSCRIPTION_LIMITS.FREE.dailyAIAnalysisLimit}회로 제한</span>
      </div>
      <div class="limit-item">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="8" y1="12" x2="16" y2="12"></line>
        </svg>
        <span>최대 {SUBSCRIPTION_LIMITS.FREE.maxTextLength}자 텍스트로 제한</span>
      </div>
    </div>
  </div>
  
  <div class="premium-benefits">
    <div class="benefits-header">
      <span class="benefits-badge">프리미엄</span>
      <span class="benefits-title">체험판과 동일한 무제한 이용</span>
    </div>
    <ul class="benefits-list">
      <li>
        <span class="benefit-icon">✓</span>
        <span>무제한 맞춤법 검사</span>
      </li>
      <li>
        <span class="benefit-icon">✓</span>
        <span>무제한 AI 분석</span>
      </li>
      <li>
        <span class="benefit-icon">✓</span>
        <span>최대 {SUBSCRIPTION_LIMITS.PREMIUM.maxTextLength.toLocaleString()}자 텍스트</span>
      </li>
      <li>
        <span class="benefit-icon">✓</span>
        <span>프리미엄 AI 모델 (GPT-4, Claude)</span>
      </li>
    </ul>
  </div>
</div>

<style>
  .trial-expiring-container {
    text-align: center;
    margin-bottom: 20px;
  }

  .trial-expiring-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    background: var(--bg-tertiary);
    border: 1px solid var(--border-primary);
    border-radius: 16px;
    margin-bottom: 12px;
  }

  .trial-expiring-badge svg {
    width: 16px;
    height: 16px;
    color: var(--text-secondary);
  }

  .trial-expiring-badge span {
    font-size: 11px;
    font-weight: 500;
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .trial-expiring-title {
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 4px 0;
  }

  .trial-expiring-subtitle {
    font-size: 14px;
    color: var(--text-secondary);
    margin: 0;
  }

  .plan-comparison {
    background: var(--bg-secondary);
    border: 1px solid var(--border-primary);
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 16px;
  }

  .comparison-header {
    margin-bottom: 12px;
  }

  .comparison-label {
    font-size: 12px;
    font-weight: 600;
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .feature-limits {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .limit-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: var(--text-primary);
    padding: 2px 0;
  }

  .limit-item svg {
    width: 16px;
    height: 16px;
    color: var(--text-tertiary);
    flex-shrink: 0;
  }

  .premium-benefits {
    border: 1px solid var(--accent-border);
    background: linear-gradient(to bottom, rgba(139, 92, 246, 0.02), rgba(99, 102, 241, 0.02));
    border-radius: 12px;
    padding: 16px;
  }

  .benefits-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
  }

  .benefits-badge {
    display: inline-block;
    padding: 2px 8px;
    background: var(--accent-primary);
    color: white;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .benefits-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--text-primary);
  }

  .benefits-list {
    list-style: none;
    padding: 0;
    margin: 0 0 12px 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .benefits-list li {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: var(--text-primary);
    padding: 2px 0;
  }

  .benefit-icon {
    color: var(--accent-primary);
    font-weight: 600;
    flex-shrink: 0;
  }
</style>